/**
 * SuperAdmin surface (`/admin/*`) -- the single source of SA route
 * definitions, wired into the aggregated router (`index.js`) the same way
 * `groups/distributor.js` and `groups/portal.js` are: spread directly (the
 * paths are already absolute `/admin/*`), NOT via `withPrefix`.
 *
 * History: PR-R1 created this file as a structural seam while the live
 * definitions stayed inline in `index.js`. That left two parallel sets of
 * `/admin/*` routes -- the inline ones (actually registered, but missing
 * `meta.layout` and missing `/admin/operations`) and this group (correct,
 * but dead). The landing-fix work reconciled them: the inline arrays were
 * deleted and this group became the single source.
 *
 * Every route carries `meta.layout = 'superadmin'` so `App.vue` mounts
 * `SuperAdminLayout.vue` (the dark SA chrome). `meta.requiredCap` is
 * informational (mirrors the `beforeEnter: requireCapability(...)` arg) --
 * enforcement remains on the per-route guard, not on the layout.
 *
 * The bare-`/admin` index (`sa_dashboard`) is defined FIRST so it wins the
 * exact-`/admin` match. A non-SA who reaches `/admin` is bounced to
 * `/distributor` by its guard's redirectTo (the deep `/admin/*` Owner-URL
 * redirects in `index.js` handle non-SA sub-paths).
 */

import { requireCapability } from "../requireCapability";
import { postLoginRedirect } from "../postLoginRedirect";
import { useAccountStore } from "@/stores/account";

import SuperAdminDashboard from "@/pages/superadmin/SuperAdminDashboard";
import DevConsole from "@/components/AdminConsole/DevConsole";
// SA Users -- platform-wide user administration across every tenant
// (https://trello.com/c/tRC6K0Zn). REPLACES the legacy
// `components/Account/AccountList` + `AccountAddEdit` pair, whose
// `/admin/accounts*` routes are now redirects (see below). Sits beside
// the capability x role matrix under the sidebar's Access Control group.
import SuperAdminUsersList from "@/pages/superadmin/UsersList.vue";
import SuperAdminUserDetail from "@/pages/superadmin/UserDetail.vue";
import SuperAdminUserCreate from "@/pages/superadmin/UserCreate.vue";
import DistributorList from "@/pages/distributor/account/DistributorList";
import DistributorAddEdit from "@/pages/distributor/account/DistributorAddEdit";
// SuperAdmin Phase 3 mock-data preview surfaces (PR #1331). Each page
// renders the shared MockDataBanner so the demo nature is visible in
// the UI; live data ships in follow-on PRs that wire the real endpoints.
import SuperAdminRiskWatch from "@/pages/superadmin/RiskWatch.vue";
import SuperAdminDistributorInvoices from "@/pages/superadmin/DistributorInvoices.vue";
// Unit 10 (payments plan): the Super Admin payments ops surfaces --
// distributor billing + exposure (OPS-01), Net order / cycle / collections
// visibility (OPS-02..04), decorator settlement (OPS-05), and the
// reconciliation drill-down (OPS-06) that 50b section 21 makes the
// acceptance test. All gated on `superadmin.settings` to MATCH the
// endpoints, which carry a distributor's receivables and the controls that
// suspend their credit.
import SuperAdminPaymentsDistributors from "@/pages/superadmin/PaymentsDistributors.vue";
import SuperAdminPaymentsSettlement from "@/pages/superadmin/PaymentsSettlement.vue";
import SuperAdminPaymentsCollections from "@/pages/superadmin/PaymentsCollections.vue";
import SuperAdminPaymentsReconciliation from "@/pages/superadmin/PaymentsReconciliation.vue";
// #669: ThreadFlow's own books (revenue, billed vs collected, Stripe check).
import SuperAdminBooks from "@/pages/superadmin/Books.vue";
import SuperAdminPlansPricing from "@/pages/superadmin/PlansPricing.vue";
// PR-6 (distributor-payments redesign, https://trello.com/c/pTVxuNGQ): the
// LIVE tier/version + grandfathering admin -- list tiers + versions, see who
// is pinned to a version, repoint distributors (single + bulk) with a
// terms-change email. Counterpart to the mock plans table on PlansPricing.
import SuperAdminMoney from "@/pages/superadmin/Money.vue";
// PR-2 P1 fix: SA sidebar entries for Access Control and Audit log
// previously 404'd because no router entries or pages existed. These
// Coming Soon stubs unblock the sidebar; the real ACL admin reskin
// and cross-tenant audit UI ship in follow-on PRs.
import SuperAdminAccessControl from "@/pages/superadmin/AccessControl.vue";
import SuperAdminAuditLog from "@/pages/superadmin/AuditLog.vue";
// PR-E (https://trello.com/c/sv7UGPvt): edit-distributor page surfaces
// the whitelisted SA-editable Distributor fields. PR-E ships the
// `timezone` column; PR-F extends with `default_brand_color`. Route
// uses `props: true` so vitest tests can pass `id` directly without
// configuring the full router.
import SuperAdminDistributorEdit from "@/pages/superadmin/DistributorEdit.vue";
// SA cross-distributor Clients list (https://trello.com/c/fyVy1dZF):
// lists clients ACROSS every distributor via the SA-gated
// GET /api/clients/superadmin-summary (PR #1781). Gated on
// superadmin.tenants to MATCH the backend endpoint's capability gate --
// the cross-tenant surface must be reachable only by a true Super Admin.
import SuperAdminClientsList from "@/pages/superadmin/ClientsList.vue";
// SA client details (Jake 2026-10-27: "SA can't edit any client details"):
// the distributor Client workspace page REUSED under /admin/clients/:id, in
// the SA layout, with surface 'superadmin' so it never links into the
// /distributor layout (https://trello.com/c/0SeIzpYd). The backend client
// endpoints it calls already admit an unscoped Super Admin.
import ClientDetailPage from "@/pages/distributor/clients/ClientDetailPage.vue";
// Decoration Model Refactor P0 (Boulders #163): SA ops-review queue
// for the new vendor_color_hex.substrate_bucket column.
import SuperAdminSubstrateBuckets from "@/pages/superadmin/SubstrateBuckets.vue";
// Decoration Model Refactor P2 PR-C (Boulders #163): SA ops-review
// surface for the placement_zone_classes taxonomy. Lets ops inspect
// the seeded classes (PR-B), override per-zone assignments, and
// re-run clustering when a new vendor catalog arrives.
import SuperAdminPlacementZoneClasses from "@/pages/superadmin/PlacementZoneClasses.vue";
// Store Decorations redesign (https://trello.com/c/oSOHJfg0): SA editor
// for the provisional ADR 0065 placement-zone printable dimensions
// (default_max_width_in / default_max_height_in). Correctable without a
// deploy; the fit-verdict math reads these columns. Gated on
// superadmin.settings (placement_zones is platform-shared reference
// data with no distributor scope).
import SuperAdminPlacementZoneDimensions from "@/pages/superadmin/PlacementZoneDimensions.vue";
// Decoration Model Refactor P2 PR-D (Boulders #163): SA editor for the
// per-method artwork resolution levels so tuning happens without a deploy
// (locked product decision #2): the recommended DPI
// (decoration_processes.dpi_enlarge_threshold), and, since card #573
// (Jake, 2026-10-28), the minimum DPI and the Reject / Warn action below it.
//
// It was HIDDEN 2026-08-27 (issue #2909) because nothing read the value.
// Something does now: services/artwork_resolution backs the low-resolution
// notices, the placement / product icons and the server's 422
// dpi_below_minimum refusal. Restored per the note that hid it.
import SuperAdminDpiThresholds from "@/pages/superadmin/DecorationProcessDpiThresholds.vue";
// SA platform settings surface (Settings tab + first toggle stub). Future
// home for feature switches + global settings. Gated on superadmin.settings
// (matches the PATCH cap the platform_settings toggle enforces; the page
// mutates SA-global platform config, same tier as the Operations / DPI /
// zone-class editors).
// Pseudo-supplier SKU setup (phase 3a/3b, design handoff
// planning/design-handoffs/handoff_2026-08-26/). SA-only per-SKU setup
// page for NON-INTEGRATED suppliers: details, sizes, colors & images,
// and (later) the zone geometry Artifi would otherwise own. Gated on
// superadmin.settings to match the neighbouring placement-zone editors
// -- placement_zone_boxes has NO distributor_id column, which is
// exactly why this surface is SA-only rather than tenant-scoped
// (ADR 0071 / research F2).
import SuperAdminSkuSetup from "@/pages/superadmin/SkuSetup.vue";
// Non-integrated suppliers (Jake's IA decision, 2026-08-26): the Tools
// entry that OWNS SKU setup. "SKU is a thing that lives under a
// supplier, not a top-level concept." Only vendors with
// `is_smart_supplier = FALSE` appear -- SanMar and S&S are configured
// through integration credentials, and their `supplier_key` is an
// external contract, not a form field.
import SuperAdminNonIntegratedSuppliers from
    "@/pages/superadmin/NonIntegratedSuppliers.vue";
import PlatformSettings from "@/pages/superadmin/PlatformSettings.vue";
// SA API Tokens -- the internal-API-token mint/reveal/revoke surface, moved
// off the Platform Settings page (#3810 shipped it as a card there; Jake asked
// for its own page 2026-09-26). Reached from a card on /admin/settings.
import SuperAdminApiTokens from "@/pages/superadmin/ApiTokens.vue";
// SuperAdmin help -- the markdown-backed in-app help surface for the
// `superadmin` audience. Thin wrapper over the ONE shared HelpRenderer,
// exactly like OwnerHelpPage and ShopperHelpPage.
import SuperAdminHelpPage from "@/pages/help/SuperAdminHelpPage.vue";
// Internal integration prototypes (Jake direction 2026-07-01). Throwaway
// capability-check / demo surfaces surfaced from the Operations sidebar's
// Prototypes section. NOT product features -- gated superadmin.settings,
// same tier as the Operations/DevConsole surface they sit beside.
import PrototypesIndex from "@/pages/prototypes/PrototypesIndex.vue";
import WilcomLetteringPrototype from "@/pages/prototypes/WilcomLetteringPrototype.vue";
import ArtifiDesignerPrototype from "@/pages/prototypes/ArtifiDesignerPrototype.vue";
// Durable SA>Developer .emb/.dst render test stage (2026-08-02, bug-fix
// companion). NOT a throwaway prototype -- reuses the drain's own render
// service so a human can verify an EMB/DST render (colorway, thumbnail vs.
// full-size, stitch count) without touching the drain's paid-call cache.
import WilcomEmbRenderStage from "@/pages/prototypes/WilcomEmbRenderStage.vue";
// TipTap rich-text editor eval prototype (2026-08-04). Throwaway surface
// that stands up the full CKEditor 5 -> TipTap (MIT) target editor --
// full toolbar + inline images wired to /api/upload_image -- to de-risk
// the swap before touching the 3 live CKEditor sites. Design doc:
// docs/planning/2026-08-04-ckeditor-to-tiptap-migration.md.
import TiptapEditorPrototype from "@/pages/prototypes/TiptapEditorPrototype.vue";
// Personal Profile page -- the ONE shared component, mounted in the
// SuperAdmin chrome (card https://trello.com/c/BxskXBad).
import PersonalProfilePage from "@/pages/PersonalProfilePage.vue";

// Role-aware fallback for bare /admin: an SA who reaches /admin lands
// on sa_dashboard; a non-SA gets routed to THEIR own role surface via
// postLoginRedirect (NOT a hardcoded /distributor that would ping-pong
// against /distributor's own campaign.edit guard for Portal / Shopper
// users -- the pre-port legacy `{path:'/admin', redirect:'/distributor'}`
// bounce only worked because redirect records bypass beforeEnter
// guards; replacing it with a static-fallback requireCapability(...)
// would reintroduce a loop for users who hold neither SA nor
// distributor caps).
function requireSACapabilityRoleAware(to, from, next) {
    const account = useAccountStore();
    const caps = account.capabilities;
    if (Array.isArray(caps) && caps.includes("superadmin.access")) {
        return next();
    }
    const dest = postLoginRedirect({ capabilities: caps || [] });
    if (dest === to.path) {
        // Defensive: postLoginRedirect returned /admin somehow (would
        // only happen if a future cap-mapping change inverts the
        // precedence). Fall through to /login to avoid a self-redirect.
        return next("/login");
    }
    return next(dest);
}

const superadminRoutes = [
    {
        // bare-`/admin` index -> the SuperAdmin Dashboard (Workstream-C
        // design handoff 2026-05-29-superadmin). Gated on superadmin.access
        // via the role-aware guard above so non-SA visitors land on THEIR
        // own role's home (/distributor, /portal, /store/:slug, or
        // /login), never on a hardcoded /distributor that would loop
        // for users who don't hold distributor.access either. This
        // REPLACES the former `{ path:'/admin', redirect:'/distributor' }`
        // bounce that forwarded Super Admins away from their own app.
        path: "/admin",
        name: "sa_dashboard",
        component: SuperAdminDashboard,
        meta: { layout: "superadmin", requiredCap: "superadmin.access" },
        beforeEnter: requireSACapabilityRoleAware,
    },
    {
        // Personal Profile (card https://trello.com/c/BxskXBad) -- the ONE
        // shared page, mounted in the SA chrome and reached from the
        // profile icon beside Log out.
        //
        // Deliberately NOT gated on superadmin.access: this is the
        // signed-in user's OWN account, not an SA resource. The backend is
        // self-scoped to the bearer token, so there is nothing here a
        // capability could protect -- and gating it would mean an admin
        // whose caps were misconfigured could not fix their own password.
        path: "/admin/profile",
        name: "sa_profile",
        component: PersonalProfilePage,
        meta: { layout: "superadmin" },
    },
    {
        // PR-R3: the canonical URL for DevConsole, surfaced in the
        // SuperAdminLayout sidebar as "Operations". This is the SOLE
        // mount -- the duplicate `/admin/developer_console` alias was
        // removed 2026-08-28 (it had zero inbound links).
        path: "/admin/operations",
        name: "sa_operations",
        component: DevConsole,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    // Legacy Subscription retirement PR 3c: the /admin/subscriptions SA CRUD
    // routes (SubscriptionList / SubscriptionAddEdit) were clean-break removed.
    // They edited the legacy ``subscriptions`` table (now dropped); the
    // pricing_plan-backed SA "Plans & Pricing" page supersedes them.
    // ---------------------------------------------------------------
    // SA Users -- platform-wide user administration
    // (https://trello.com/c/tRC6K0Zn)
    //
    // `/admin/users/new` is declared BEFORE `/admin/users/:id` so the
    // literal `new` segment wins the match and is never parsed as an
    // account id.
    //
    // Read is gated on superadmin.access; the create + detail pages are
    // gated on superadmin.settings because both MUTATE. The route guard
    // is a UX affordance only -- every write is independently gated
    // server-side in `superadmin_users.py`.
    // ---------------------------------------------------------------
    {
        path: "/admin/users",
        name: "sa_users",
        component: SuperAdminUsersList,
        meta: { layout: "superadmin", requiredCap: "superadmin.access" },
        beforeEnter: requireCapability("superadmin.access", "/admin"),
    },
    {
        path: "/admin/users/new",
        name: "sa_user_create",
        component: SuperAdminUserCreate,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    {
        path: "/admin/users/:id",
        name: "sa_user_detail",
        component: SuperAdminUserDetail,
        // Route params as props so tests can mount with `props: { id }`
        // without configuring a full router (same idiom as
        // sa_distributor_edit).
        props: true,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    // Retirement of the legacy `/admin/accounts*` surface. The old
    // AccountList / AccountAddEdit pages are superseded by the Users
    // pages above; these redirects keep bookmarks, deep links, and any
    // stale `$router.push('/admin/accounts/...')` call sites working.
    // Redirect records bypass beforeEnter guards by design -- the
    // destination route's own guard runs instead.
    { path: "/admin/accounts", redirect: "/admin/users" },
    {
        // `?distributor_id=N` is carried through: the distributor pages
        // link here as "add an account for THIS distributor", and
        // dropping the param would silently lose the prefill that made
        // the link useful. UserCreate reads it to preselect tenancy.
        path: "/admin/accounts/add",
        redirect: (to) => ({ path: "/admin/users/new", query: to.query }),
    },
    {
        path: "/admin/accounts/:id",
        redirect: (to) => ({ path: `/admin/users/${to.params.id}` }),
    },
    {
        path: "/admin/distributors",
        name: "distributor_list",
        component: DistributorList,
        meta: { layout: "superadmin", requiredCap: "superadmin.tenants" },
        beforeEnter: requireCapability("superadmin.tenants", "/admin"),
    },
    {
        path: "/admin/distributors/add",
        name: "distributor_add",
        component: DistributorAddEdit,
        meta: { layout: "superadmin", requiredCap: "superadmin.tenants" },
        beforeEnter: requireCapability(
            "superadmin.tenants",
            "/admin/distributors"
        ),
    },
    {
        // PR-E (https://trello.com/c/sv7UGPvt): the edit-distributor
        // page for the PR-E whitelisted Distributor fields (today:
        // `timezone`). Registered BEFORE the bare `/admin/distributors/:id`
        // route so the literal `/edit` suffix wins the match.
        path: "/admin/distributors/:id/edit",
        name: "sa_distributor_edit",
        component: SuperAdminDistributorEdit,
        // Pass route params as component props so vitest tests can mount
        // with `props: { id: '42' }` without a router instance.
        props: true,
        meta: { layout: "superadmin", requiredCap: "superadmin.access" },
        beforeEnter: requireCapability("superadmin.access", "/admin"),
    },
    {
        // Redirect bare /admin/distributors/:id -> /admin/distributors/:id/edit
        // (the SA-specific edit page). The old legacy DistributorAddEdit
        // component at this path is replaced by SuperAdminDistributorEdit
        // at the /edit suffix (PR-E). Deep-links from external tools,
        // email notifications, or the old route name "distributor_edit"
        // land here and are forwarded to the correct surface.
        path: "/admin/distributors/:id",
        redirect: (to) => ({
            path: `/admin/distributors/${to.params.id}/edit`,
        }),
    },
    {
        // SA cross-distributor Clients list. Gated on superadmin.tenants
        // to MATCH the backend GET /api/clients/superadmin-summary gate
        // (the cross-tenant surface must be SA-only). A distinct literal
        // path -- no precedence conflict with /admin/distributors/:id.
        path: "/admin/clients",
        name: "sa_clients",
        component: SuperAdminClientsList,
        meta: { layout: "superadmin", requiredCap: "superadmin.tenants" },
        beforeEnter: requireCapability("superadmin.tenants", "/admin"),
    },
    {
        path: "/admin/clients/:id(\\d+)",
        name: "sa_client_detail",
        component: ClientDetailPage,
        props: (route) => ({ id: route.params.id, surface: "superadmin" }),
        meta: { layout: "superadmin", requiredCap: "superadmin.tenants" },
        beforeEnter: requireCapability("superadmin.tenants", "/admin"),
    },
    // PR #1331 Phase 3 mock-data preview surfaces. Each route gates on
    // superadmin.access (the SA entry capability seeded by PR #1325).
    {
        path: "/admin/risk-watch",
        name: "sa_risk_watch",
        component: SuperAdminRiskWatch,
        meta: { layout: "superadmin", requiredCap: "superadmin.access" },
        beforeEnter: requireCapability("superadmin.access", "/admin"),
    },
    // Unit 7: the 1st/15th distributor-invoice review and release surface
    // (OPS-08). Gated on ``superadmin.settings`` rather than the lesser
    // ``superadmin.access`` used by the read-only preview pages above,
    // because releasing a batch MOVES MONEY -- it matches the capability
    // the endpoints themselves require. B6 asks for a named owner; until
    // that is answered the high-watermark cap is the conservative choice.
    {
        path: "/admin/distributor-invoices",
        name: "sa_distributor_invoices",
        component: SuperAdminDistributorInvoices,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    // Unit 10 -- the payments ops surfaces. Same capability as the Unit 7
    // invoice page above and the same reasoning: these read a distributor's
    // receivables and BILLING identifiers, and the OPS-07 controls suspend
    // credit and hold settlements. B6 asks for a named owner; until that is
    // answered the high-watermark cap is the conservative choice.
    {
        path: "/admin/payments/distributors",
        name: "sa_payments_distributors",
        component: SuperAdminPaymentsDistributors,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    {
        path: "/admin/payments/settlement",
        name: "sa_payments_settlement",
        component: SuperAdminPaymentsSettlement,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    {
        path: "/admin/payments/collections",
        name: "sa_payments_collections",
        component: SuperAdminPaymentsCollections,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    // #669 Books: ThreadFlow's own money. Same capability as the sibling
    // payments pages (it reads every distributor's ledger and can call Stripe).
    {
        path: "/admin/payments/books",
        name: "sa_payments_books",
        component: SuperAdminBooks,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    // Two entries so the drill-down is reachable BOTH as a bare lookup form
    // and as a deep link to one order. The deep link is what makes a
    // reconciliation shareable -- an operator sends the URL rather than
    // describing which order they mean.
    {
        path: "/admin/payments/reconciliation",
        name: "sa_payments_reconciliation",
        component: SuperAdminPaymentsReconciliation,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    {
        path: "/admin/payments/reconciliation/:orderId",
        name: "sa_payments_reconciliation_order",
        component: SuperAdminPaymentsReconciliation,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    {
        path: "/admin/plans-pricing",
        name: "sa_plans_pricing",
        component: SuperAdminPlansPricing,
        meta: { layout: "superadmin", requiredCap: "superadmin.access" },
        beforeEnter: requireCapability("superadmin.access", "/admin"),
    },
    {
        path: "/admin/money",
        name: "sa_money",
        component: SuperAdminMoney,
        meta: { layout: "superadmin", requiredCap: "superadmin.access" },
        beforeEnter: requireCapability("superadmin.access", "/admin"),
    },
    // PR-2 P1 fix: stub destinations for the SA sidebar's Access
    // Control and Audit log entries. Gated on superadmin.access (the
    // SA entry capability seeded by PR #1325) for consistency with
    // the other Phase 3 mock-data preview surfaces above.
    {
        path: "/admin/access-control",
        name: "sa_access_control",
        component: SuperAdminAccessControl,
        meta: { layout: "superadmin", requiredCap: "superadmin.access" },
        beforeEnter: requireCapability("superadmin.access", "/admin"),
    },
    {
        path: "/admin/audit",
        name: "sa_audit_log",
        component: SuperAdminAuditLog,
        meta: { layout: "superadmin", requiredCap: "superadmin.access" },
        beforeEnter: requireCapability("superadmin.access", "/admin"),
    },
    // Vendor catalog UI removed 2026-08-28 (Jake: "vendors is legacy OW
    // stuff and can go"). The `/admin/vendors*` routes and their
    // VendorList / VendorAddEdit components are gone, along with the
    // `/distributor/vendors*` backward-compat redirects in
    // `router/index.js` -- their target no longer exists. The `vendors`
    // TABLE is untouched; this removed the legacy admin UI, not the data.
    // The modern supplier surface is `/admin/suppliers/non-integrated`.
    // Decoration Model Refactor P0 (Boulders #163): substrate-bucket
    // ops-review queue. Gated on superadmin.settings (matches the
    // vendor_color_hex write tier; non-SA can't read this page either,
    // because the queue exposes platform-shared reference data the
    // editor here mutates).
    {
        path: "/admin/vendor-color-hex/substrate-buckets",
        name: "sa_substrate_buckets",
        component: SuperAdminSubstrateBuckets,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    // Decoration Model Refactor P2 PR-C (Boulders #163): zone-class
    // ops-review. Gated on superadmin.settings (matches the
    // placement-zone write tier; the taxonomy is platform-shared
    // reference data).
    {
        path: "/admin/placement-zone-classes",
        name: "sa_placement_zone_classes",
        component: SuperAdminPlacementZoneClasses,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    // Store Decorations redesign (https://trello.com/c/oSOHJfg0): SA
    // editor for the provisional ADR 0065 placement-zone printable
    // dimensions. A distinct literal `/dimensions` suffix -- no
    // precedence conflict with /admin/placement-zone-classes. Gated on
    // superadmin.settings (matches the PATCH cap the dims editor
    // enforces; placement_zones is platform-shared reference data).
    {
        path: "/admin/placement-zones/dimensions",
        name: "sa_placement_zone_dimensions",
        component: SuperAdminPlacementZoneDimensions,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    // Decoration Model Refactor P2 PR-D (Boulders #163): DPI threshold
    // editor for decoration_processes. Gated on superadmin.settings
    // (decoration_processes is platform-shared reference data; only
    // SA may mutate).
    // Non-integrated suppliers, and SKU setup NESTED beneath one.
    //
    // This replaces `/admin/suppliers/local/sku-setup`, where the
    // literal `local` segment named a CLASS in the position an IDENTITY
    // belongs (Jake, 2026-08-26). A SKU means nothing until you know
    // whose SKU it is, so the supplier id is now in the path and the
    // page reads its supplier from route context rather than a
    // hardcoded fixture string.
    //
    // The old URL is kept as a REDIRECT below rather than deleted: it
    // is linked from the phase 3a/3b ui-scenarios and from anyone's
    // bookmarks, and a 404 there would look like the feature was
    // removed. It resolves to the list, which is the honest
    // destination -- it cannot guess WHICH supplier was meant.
    {
        path: "/admin/suppliers/non-integrated",
        name: "sa_non_integrated_suppliers",
        component: SuperAdminNonIntegratedSuppliers,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    {
        path: "/admin/suppliers/non-integrated/:supplierId/sku-setup",
        name: "sa_sku_setup",
        component: SuperAdminSkuSetup,
        // Route params as props so the page takes its supplier from
        // route context and vitest can mount it with
        // `props: { supplierId }` without a router instance -- the same
        // idiom as sa_distributor_edit and sa_user_detail.
        props: true,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    {
        path: "/admin/suppliers/local/sku-setup",
        redirect: "/admin/suppliers/non-integrated",
    },
    // Artwork resolution levels per decoration method (recommended DPI,
    // minimum DPI, Reject / Warn). Reached from a card on Platform Settings.
    // Restored for card #573 after being hidden 2026-08-27 (issue #2909);
    // gated like every other platform reference-data editor.
    {
        path: "/admin/decoration-processes/dpi-thresholds",
        name: "sa_dpi_thresholds",
        component: SuperAdminDpiThresholds,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    // SA platform settings surface (Settings tab + first toggle stub).
    // Gated on superadmin.settings -- the page mutates SA-global platform
    // config (the hide_unpreviewable_color_variants toggle PATCH enforces
    // the same cap), so a non-SA must not reach the surface at all.
    {
        path: "/admin/settings",
        name: "sa_settings",
        component: PlatformSettings,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    // SA API Tokens (`/admin/api-tokens`). The internal-API-token surface,
    // moved off the Platform Settings page where #3810 shipped it as a card.
    //
    // THE SAME CAPABILITY AND THE SAME GUARD AS /admin/settings, deliberately
    // and not by coincidence. This page MINTS CREDENTIALS a partner
    // authenticates with, so it must not be reachable more easily than the
    // settings page the card came off -- and the five endpoints behind it are
    // all gated on `superadmin.settings` anyway, so a looser route gate would
    // only produce a page that renders and then 403s on every action.
    //
    // A NEW capability was considered and rejected: it would have to be
    // granted somewhere, and until it is the page is unreachable for everyone
    // including Super Admin. `superadmin.settings` is already the
    // high-watermark cap these endpoints check.
    //
    // The gate is asserted by INVOKING this route's own guard, with its
    // control, in router/__tests__/superadmin-api-tokens-route.spec.js -- the
    // sibling specs' `typeof beforeEnter === "function"` cannot tell this
    // capability from a weaker one.
    {
        path: "/admin/api-tokens",
        name: "sa_api_tokens",
        component: SuperAdminApiTokens,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    // SuperAdmin help (`/admin/help/superadmin[/:slug]`). The articles
    // shipped before any way to reach them existed: the API already served
    // the `superadmin` audience and HelpRenderer already accepted it, but the
    // SPA had no route, so a SuperAdmin could not navigate to their own
    // documentation.
    //
    // GATED ON `help.access`, NOT on `superadmin.*`. This is the same gate
    // the owner help routes use, and it is the correct one: reading the
    // manual is not a platform-settings action, so gating it on
    // `superadmin.settings` would quietly make documentation a
    // higher-privilege surface than the console it documents. Safe because
    // Super Admin holds EVERY capability in the registry -- the `all_keys`
    // rule in `api/src/seeders/capability.py`, which `help.access` is part of
    // (verified in `api/tests/test_capability_model.py`). The mount point,
    // not the capability, is what keeps these staff-only: `/admin/*` sits
    // behind the SA auth boundary, unlike the deliberately-public
    // `/help/shopper`.
    //
    // ROUTES DERIVE FROM FILES, same design decision as the owner routes:
    // `:slug` is a parameter resolved against `GET /api/help/superadmin/
    // <slug>`, NOT a hand-maintained table. Adding
    // `docs/user/superadmin/<name>.md` publishes a page with no route edit.
    {
        path: "/admin/help/superadmin",
        name: "superadmin_help_index",
        component: SuperAdminHelpPage,
        meta: { layout: "superadmin", requiredCap: "help.access" },
        beforeEnter: requireCapability("help.access", "/admin"),
    },
    {
        path: "/admin/help/superadmin/:slug",
        name: "superadmin_help_article",
        component: SuperAdminHelpPage,
        meta: { layout: "superadmin", requiredCap: "help.access" },
        beforeEnter: requireCapability("help.access", "/admin"),
    },
    // Internal integration prototypes (Jake direction 2026-07-01). All
    // gated superadmin.settings (the Operations/DevConsole tier). These
    // are throwaway capability-check surfaces, not product features.
    {
        path: "/admin/prototypes",
        name: "sa_prototypes",
        component: PrototypesIndex,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    {
        path: "/admin/prototypes/wilcom-lettering",
        name: "sa_prototype_wilcom_lettering",
        component: WilcomLetteringPrototype,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    {
        // Artifi Phase B Designer spike (PR #1773). Brought onto main so
        // the Prototypes directory links a real page; the canvas does not
        // yet paint (blocked on an Amla integration doc). WIP by design.
        path: "/admin/prototypes/artifi-designer",
        name: "sa_prototype_artifi_designer",
        component: ArtifiDesignerPrototype,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    {
        // Durable SA>Developer .emb/.dst render test stage (2026-08-02).
        // NOT a throwaway prototype -- ships alongside the colorwaytype
        // fix so the render can be re-verified by hand at any time.
        path: "/admin/prototypes/wilcom-emb-render-stage",
        name: "sa_prototype_wilcom_emb_render_stage",
        component: WilcomEmbRenderStage,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
    {
        // TipTap rich-text editor eval prototype (2026-08-04). Throwaway
        // surface proving the CKEditor 5 -> TipTap (MIT) swap incl. inline
        // images against /api/upload_image. Same superadmin.settings gate
        // as the sibling prototype routes.
        path: "/admin/prototypes/tiptap-editor",
        name: "sa_prototype_tiptap_editor",
        component: TiptapEditorPrototype,
        meta: { layout: "superadmin", requiredCap: "superadmin.settings" },
        beforeEnter: requireCapability("superadmin.settings", "/admin"),
    },
];

export default superadminRoutes;
